// admin-validate.js
//
// Shared response-shape validation + failure-mapping for the restyled admin
// screens. Created in Task 3.1 (Step 3d) — NOT in the Dashboard rewrite —
// because Feature Flags (4.3), Rooms (5.1), and Database (5.2) import it and run
// BEFORE the Dashboard rewrite (which is sequenced last). Every loader/action
// validates a response's top-level fields AND every rendered nested row at the
// exact type BEFORE rendering (a malformed HTTP-200 is a structured shape error
// for that band, never rendered as valid), and maps every caught fetch/await
// rejection through `toError` so `kind`/`status` are consistent everywhere.

// kinds: num=Number.isFinite, str=string, bool=boolean, arr=Array, obj=non-null object.
const nullableStr = (v) => v === null || typeof v === 'string';
const optionalStr = (v) => v === undefined || nullableStr(v);
// A timestamp must be RENDERABLE as a real date: a string OR number that yields a
// finite, in-range `new Date(v).getTime()`. This rejects an unparseable string AND a
// finite-but-out-of-range number (e.g. 1e308, which is finite yet produces an Invalid
// Date and would otherwise render a bogus "<1m" age or a silent em dash). Anything
// else (null/boolean/object) is rejected by the type guard. A malformed value fails
// the shape check so the loader surfaces a structured contract error rather than
// rendering it as valid data (formatAge/formatTime keep an em-dash fallback for direct
// calls as defense in depth).
const timestamp = (v) => (typeof v === 'string' || typeof v === 'number') && Number.isFinite(new Date(v).getTime());
const stringArray = (v) => Array.isArray(v) && v.every((x) => typeof x === 'string');
const roomPlayers = (v) => Array.isArray(v) && v.every((p) => p && typeof p === 'object' && !Array.isArray(p)
  && Number.isFinite(p.slot) && nullableStr(p.username) && nullableStr(p.userId));
const qobsPool = (v) => v && typeof v === 'object' && !Array.isArray(v)
  && typeof v.readonlyConfigured === 'boolean'
  && Number.isFinite(v.rw?.total) && Number.isFinite(v.rw?.waiting)
  && (!v.readonlyConfigured || (Number.isFinite(v.ro?.total) && Number.isFinite(v.ro?.waiting)));
const warningArray = (v) => Array.isArray(v) && v.every((w) => w && typeof w === 'object'
  && !Array.isArray(w) && typeof w.message === 'string');
export const ROOM_ROW = {
  code:'str', hostUsername:nullableStr, romTitle:nullableStr, system:nullableStr, type:'str', players:roomPlayers,
  spectatorCount:'num', local:'bool', replicaId:'str', createdAt:timestamp,
};
export const TABLE_ROW  = { name:'str', row_count:'num', total_bytes:'num', is_hypertable:'bool' };
export const DAU_ROW    = { day:'str', dau:'num', wau:'num', mau:'num' };        // sparkline series = rows.map(r => r.dau)
export const SIGNUP_ROW = { day:'str', signups:'num', verified_signups:'num' }; // sparkline series = rows.map(r => r.signups)
export const QOBS_ROW = {
  query:'str', calls:'num', total_exec_time_ms:'num', mean_exec_time_ms:'num', rows:'num',
  shared_blks_hit:'num', shared_blks_read:'num', local_blks_hit:'num', local_blks_read:'num',
  temp_blks_read:'num', temp_blks_written:'num',
};
export const ROUTE_METRIC_ROW = {
  timestamp, route:'str', durationMs:'num', status:'num', queryCount:'num', cacheResult:'str',
  pools:stringArray, poolWaiting:'num', replicaId:'str',
};
export const SLOW_QUERY_ROW = { sql:'str', route:'str', durationMs:'num', timestamp, pool:'str' };
// Each entry: { top?: {path:kind}, rowsAt?: <key|null for the response itself>, rowSpec?: {field:kind} }.
export const SCHEMAS = {
  '/api/admin/dashboard-stats':     { top: { mod_queue:'num', open_reports:'num', contact_pending:'num', active_bans:'num', client_errors_24h:'num', new_users_7d:'num', sessions_today:'num' } },
  '/api/analytics/summary':         { top: { users:'num', sessions:'num', active_today:'num', total_play_seconds:'num', achievement_unlocks:'num' } },  // only the KPIs the band renders (no `returning`)
  '/api/analytics/dau':             { rowsAt: null, rowSpec: DAU_ROW },     // response itself is the array
  '/api/analytics/signups':         { rowsAt: null, rowSpec: SIGNUP_ROW },
  '/api/admin/rooms':               { top: { 'summary.total':'num', 'summary.local':'num', 'summary.remote':'num' }, rowsAt: 'rooms', rowSpec: ROOM_ROW },
  '/api/admin/table-sizes':         { rowsAt: 'tables', rowSpec: TABLE_ROW },
  '/api/admin/query-observability': {
    top: { available:'bool', reason:optionalStr, 'coverage.sampledHours':'num', 'coverage.complete':'bool', pool:qobsPool, warnings:warningArray, replicaId:'str' },
    collections: [ { at:'queries', spec:QOBS_ROW }, { at:'routeMetrics', spec:ROUTE_METRIC_ROW } ],
  },
  '/api/admin/slow-queries': {
    top: {
      'stats.totalTracked':'num', 'stats.bufferCapacity':'num', 'stats.threshold':'num',
      'stats.totalQueriesSeen':'num', 'stats.p50':'num', 'stats.p95':'num', 'stats.p99':'num',
    },
    rowsAt:'queries', rowSpec:SLOW_QUERY_ROW,
  },
  '/api/admin/memory/summary':      { top: { pressure_events_today:'num', mitigation_events_today:'obj', mitigation_total_today:'num', tab_kill_crashes_today:'num', active_sessions_today:'num' } },
  '/api/admin/slow-queries/clear':  { top: { ok:'bool' } },
  '/api/admin/slow-queries/config': { top: { threshold:'num' } },
  '/api/admin/refresh-roms':        { top: { systems:'arr' } },
};
export const typeOk = (kind, v) =>
  typeof kind === 'function' ? kind(v)
  : kind === 'num' ? Number.isFinite(v) : kind === 'str' ? typeof v === 'string'
  : kind === 'bool' ? typeof v === 'boolean' : kind === 'arr' ? Array.isArray(v)
  : kind === 'obj' ? (v != null && typeof v === 'object' && !Array.isArray(v)) : v != null;   // obj REJECTS arrays
const dig = (o, path) => path.split('.').reduce((x, k) => (x == null ? x : x[k]), o);
function checkRow(row, spec, endpoint, i) {
  for (const [f, kind] of Object.entries(spec)) if (!typeOk(kind, row?.[f])) return { ok: false, error: { kind: 'shape', endpoint, status: 200, message: `row[${i}].${f}`, cause: null } };
  return { ok: true };
}
export function validateShape(endpoint, res) {
  const key = String(endpoint).split(/[?#]/)[0];   // NORMALIZE: strip query/hash (e.g. `/api/admin/query-observability?limit=50`) so a paginated GET matches its base-path schema
  const s = SCHEMAS[key];
  if (!s) return { ok: false, error: { kind: 'unknown', endpoint: key, status: null, cause: new Error(`no schema registered for ${key}`) } };   // UNKNOWN-schema guard → FAIL-CLOSED (never a `Cannot read properties of undefined` TypeError, never a silent pass)
  for (const [path, kind] of Object.entries(s.top || {})) if (!typeOk(kind, dig(res, path))) return { ok: false, error: { kind: 'shape', endpoint: key, status: 200, message: `top.${path}`, cause: null } };
  // A response may carry MULTIPLE row collections (e.g. query-observability has `queries` AND `routeMetrics`).
  const cols = s.collections || (s.rowSpec ? [{ at: s.rowsAt, spec: s.rowSpec }] : []);
  for (const { at, spec } of cols) {
    const rows = at == null ? res : dig(res, at);
    if (!Array.isArray(rows)) return { ok: false, error: { kind: 'shape', endpoint: key, status: 200, message: `${at ?? 'response'} not array`, cause: null } };
    for (let i = 0; i < rows.length; i++) { const r = checkRow(rows[i], spec, key, i); if (!r.ok) return r; }
  }
  return { ok: true };
}
// Centralized failure mapping — used at EVERY loader/action so `kind`/`status` are consistent.
// The admin api throws `ApiError` (from src/shared/api-fetch.js) carrying the real HTTP `.status`;
// a fetch network failure throws TypeError; an abort/deadline throws AbortError/TimeoutError;
// a bad body on an otherwise-OK (2xx) response (JSON.parse / shape) → kind 'shape' with status 200.
// NOTE the depth: this file is apps/admin/src/lib/, so the repo-root shared core is FOUR levels up.
import { ApiError } from '../../../../src/shared/api-fetch.js';
export function toError(endpoint, e) {
  if (e instanceof ApiError) return { ok: false, error: { kind: 'http', endpoint, status: e.status, cause: e } };
  if (e?.name === 'TimeoutError') return { ok: false, error: { kind: 'timeout', endpoint, status: null, cause: e } };
  if (e?.name === 'AbortError') return { ok: false, error: { kind: 'cancelled', reason: e.message === 'navigation' ? 'navigation' : 'aborted', endpoint, status: null, cause: e } };
  if (e instanceof TypeError) return { ok: false, error: { kind: 'network', endpoint, status: null, cause: e } };   // fetch() network failure throws TypeError
  if (e instanceof SyntaxError) return { ok: false, error: { kind: 'shape', endpoint, status: 200, cause: e } };    // JSON.parse of a received 2xx body failed
  return { ok: false, error: { kind: 'unknown', endpoint, status: null, cause: e ?? null } };   // an UNEXPECTED error — surfaced as 'unknown', never mislabeled as a data-shape error
}
